import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1574848296471-28f79a036f79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwZ2VvbWV0cmljJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc4MjA3OTQ1N3ww&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      navigate("/portal");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const inp = "w-full bg-white border border-zinc-300 rounded-sm px-4 py-3 text-sm focus:border-coral focus:ring-1 focus:ring-coral outline-none transition";

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/65" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <blockquote className="font-serif text-3xl font-medium leading-snug text-balance">"Buy better, faster, with confidence."</blockquote>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-12 bg-background">
        <div className="w-full max-w-sm">
          <Logo />
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-coral mt-8 transition" data-testid="back-home">
            <ArrowLeft size={15} /> Back to site
          </Link>
          <h1 className="font-serif text-4xl font-bold mt-6 tracking-tight">Create your account.</h1>
          <p className="text-zinc-500 mt-2">Submit and track sourcing requests in your portal.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-2.5 rounded-sm" data-testid="register-error">{error}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-sm font-semibold">Full name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`${inp} mt-1.5`} data-testid="register-name" />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-semibold">Work email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inp} mt-1.5`} data-testid="register-email" />
              </div>
              <div className="col-span-2">
                <label className="text-sm font-semibold">Password</label>
                <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={`${inp} mt-1.5`} data-testid="register-password" />
              </div>
              <div>
                <label className="text-sm font-semibold">Company</label>
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={`${inp} mt-1.5`} data-testid="register-company" />
              </div>
              <div>
                <label className="text-sm font-semibold">Phone</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`${inp} mt-1.5`} data-testid="register-phone" />
              </div>
            </div>
            <button disabled={loading} className="w-full bg-coral hover:bg-coral-hover text-white font-semibold py-3 rounded-sm transition disabled:opacity-60 flex items-center justify-center gap-2" data-testid="register-submit">
              {loading ? "Creating..." : "Create account"} <ArrowRight size={17} />
            </button>
          </form>
          <p className="text-sm text-zinc-500 mt-6">
            Already have an account? <Link to="/login" className="text-coral font-semibold hover:underline" data-testid="goto-login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
